import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import ScrollToSectionButton from '../components/ScrollToSectionButton';
import NewsletterForm_WithPurchase from '../components/NewsletterForm_WithPurchase';


const HeroSection = () => {
  return (
    <section 
      className="relative flex items-center bg-beige-light"
      style={{ minHeight: 'var(--content-height)' }}
    >
      <div 
        className="absolute inset-0 bg-cover bg-top bg-no-repeat md:hidden"
        style={{ 
          backgroundImage: 'url("/images/ebook/cucina-consapevole-facile/copertina-ebook-mobile.webp")',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-beige-light/75 to-beige-light"></div>
      </div>
      
      <div className="container relative z-10 mx-auto px-4 flex flex-col md:flex-row items-start">
        <div className="md:hidden flex flex-col justify-center w-full" style={{ minHeight: 'calc(var(--content-height) - 0rem)' }}>
          <div className="p-4 rounded-t-3xl">
            <h1 className="h1 text-green mb-4">
              La Cucina Consapevole Facile
            </h1>
            <p className="body text-text-dark mt-4 mb-6">
              Scopri come portare benessere e gusto nella tua vita con il nostro eBook gratuito. 
              Iscriviti alla newsletter per riceverlo subito!
            </p>
            <ScrollToSectionButton 
              targetId="newsletter"
              className="flex h4 items-center justify-center px-6 py-5 w-full bg-beige-dark text-beige-light font-bold rounded-full hover:bg-accent transition-colors duration-300 shadow-md"
            >
              Scarica l'eBook gratuito
              <ChevronDown className="ml-2" size={20} />
            </ScrollToSectionButton>
          </div>
        </div>

        <div className="hidden md:flex md:w-1/2 md:pr-8 flex-col">
          <h1 className="h1 text-green mb-4">
            La Cucina Consapevole Facile
          </h1>
          <p className="body text-text-dark mt-10 mb-10">
            Scopri come il cibo può diventare una fonte di energia, benessere e gioia per la tua vita.
          </p>
          <ScrollToSectionButton 
            targetId="newsletter"
            className="flex h4 items-center justify-center px-6 py-5 mt-4 bg-beige-dark text-beige-light font-bold rounded-full hover:bg-accent transition-colors duration-300 shadow-md"
          >
            Scarica l'eBook gratuito
            <ChevronDown className="ml-2" size={20} />
          </ScrollToSectionButton>
        </div>
        <div className="hidden md:block md:w-1/2 mt-8 md:mt-0">
          <div className="rounded-15 aspect-[3/4] w-full max-w-md mx-auto overflow-hidden">
            <img 
              src="/images/ebook/cucina-consapevole-facile/copertina-ebook.webp" 
              alt="Copertina dell'eBook La Cucina Consapevole"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </div>
    </section>
  );
};



const EbookFeatures = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  
  const features = [
    {
      id: 1,
      title: "Principi per il benessere",
      description: "Strategie pratiche per bilanciare corpo, mente ed energia attraverso il cibo.",
      icon: "✨"
    },
    {
      id: 2,
      title: "Ricette semplici e gustose",
      description: "Piatti che ti aiuteranno a nutrirti con consapevolezza, senza rinunciare al piacere.",
      icon: "🍽️"
    },
    {
      id: 3,
      title: "Ingredienti naturali e stagionali",
      description: "Guida alla scelta degli alimenti più adatti per ogni periodo dell'anno.",
      icon: "🌱"
    },
    {
      id: 4,
      title: "Evitare errori comuni",
      description: "Strategie per iniziare con sicurezza il tuo percorso di alimentazione consapevole.",
      icon: "💡"
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
      {features.map((feature) => (
        <div
          key={feature.id}
          className="p-6 bg-green rounded-15 shadow-md transition-all duration-300"
          onMouseEnter={() => setHoveredCard(feature.id)}
          onMouseLeave={() => setHoveredCard(null)}
          style={{
            transform: hoveredCard === feature.id ? 'translateY(-8px)' : 'translateY(0)',
            boxShadow: hoveredCard === feature.id ? '0 10px 25px rgba(0, 0, 0, 0.1)' : ''
          }}
        >
          <div className="flex items-center mb-4">
            <span className="text-3xl mr-3 text-beige-light">{feature.icon}</span>
            <h3 className="h4 text-beige-light">{feature.title}</h3>
          </div>
          <p className="body-tile text-text-light">{feature.description}</p>
        </div>
      ))}
    </div>
  );
};


const EbookSection = () => {
  const productId = "ee496870-99ca-4736-a1e5-b2d66315a048";

  const handleSubmissionComplete = (result) => {
    console.log('Newsletter submission completed:', result);
  };

  return (
    <>
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="h2 text-green text-center mb-16">Cosa troverai nel tuo eBook gratuito?</h2>
            <EbookFeatures />
            <div className="text-center mb-12">
              <h3 className="h3 text-green mb-4">🌿 Perché è importante?</h3>
              <p className="body text-text-dark">
                Il cibo non è solo nutrimento, è energia, cura e connessione. Attraverso la cucina consapevole puoi migliorare la tua vitalità, ridurre lo stress e portare maggiore armonia nella tua vita.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="newsletter" className="bg-green py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div id="spiegazione" className="bg-beige-dark p-8 rounded-15 shadow-lg">
              <NewsletterForm_WithPurchase 
                productId={productId}
                onSubmissionComplete={handleSubmissionComplete}
                title="Iscriviti oggi e ricevi il tuo eBook gratuito!"
                description="Iscrivendoti oggi riceverai l'ebook Cucina Consapevole Facile direttamente alla tua email"
                buttonText="ISCRIVITI E SCARICA"
              />
              <p className="text-beige-light text-sm text-center mt-4">
                Riceverai anche la mia newsletter con consigli esclusivi sulla medicina integrata e il benessere
              </p>
            </div>

            <div className="text-center mt-12">
              <p className="h4 text-beige-light">"Prendersi cura di sé attraverso il cibo è il primo passo per una vita più equilibrata"</p>
              <p className="body-tile text-text-light italic mt-4">
                Dott.ssa Alessandra Filzoli<br />
                Medico di Medicina Integrata e Medicina Tradizionale Cinese
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const EbookLandingPage = () => {
  return (
    <div className="flex flex-col">
      <HeroSection />
      <EbookSection />
    </div>
  );
};

export default EbookLandingPage;